// ============================================================
// membership.js
//
// Pure helpers + Firestore access layer for the new
// `teams/{teamId}/members/{memberId}` data model.
//
// Kept intentionally free of JSX, component state, UI text and
// non-ASCII characters. All user-facing strings live in the
// components that call these functions.
//
// Contract:
//   - memberId is derived from email (lowercased, slashes replaced).
//   - A "shadow" member is one that has no linked auth user yet
//     (uid === null, claimed === false).
//   - A "claimed" member has { uid, claimed: true, claimedAt }.
//   - Stats fields (goals / assists / matchesPlayed / stats) are
//     initialized to zero on creation and are updated elsewhere
//     (match write flows, AdminPanel manual overrides).
// ============================================================

import {
  db,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  getDocs,
  collectionGroup,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from '../firebase';

// ------------------------------------------------------------
// Pure helpers (no Firestore)
// ------------------------------------------------------------

export const normalizeEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email.trim().toLowerCase();
};

// Firestore doc IDs cannot contain '/' and must be <= 1500 bytes.
// Emails are safe except for '/', which we replace with '_'.
export const memberIdFromEmail = (email) => {
  const normalized = normalizeEmail(email);
  if (!normalized) return '';
  return normalized.replace(/\//g, '_');
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (email) => {
  const normalized = normalizeEmail(email);
  return EMAIL_RE.test(normalized);
};

const getAllTeamIds = async () => {
  const slugsSnap = await getDocs(collection(db, 'slugs'));
  return Array.from(
    new Set(
      slugsSnap.docs
        .map((entry) => entry.data()?.teamId)
        .filter(Boolean)
    )
  );
};

// ------------------------------------------------------------
// Firestore operations
// ------------------------------------------------------------

const ensureLegacyPlayerRecord = async (
  teamId,
  { email, displayName }
) => {
  const normalized = normalizeEmail(email);
  const trimmedName = (displayName || '').trim();

  const playersRef = collection(db, 'teams', teamId, 'players');
  const [byEmailSnap, byNameSnap] = await Promise.all([
    normalized
      ? getDocs(query(playersRef, where('email', '==', normalized)))
      : Promise.resolve({ empty: true, docs: [] }),
    trimmedName
      ? getDocs(query(playersRef, where('name', '==', trimmedName)))
      : Promise.resolve({ empty: true, docs: [] }),
  ]);

  const existing = byEmailSnap.docs[0] || byNameSnap.docs[0];
  if (existing) {
    const data = existing.data();
    if (!data.email && normalized) {
      await updateDoc(existing.ref, { email: normalized });
    }
    return { legacyPlayerId: existing.id, created: false };
  }

  const created = await addDoc(playersRef, {
    name: trimmedName || normalized,
    email: normalized || null,
    goals: 0,
    assists: 0,
    matchesPlayed: 0,
    stats: {},
    createdAt: serverTimestamp(),
  });

  return { legacyPlayerId: created.id, created: true };
};

/**
 * Create or upsert a member slot under a team.
 * If a slot with the same memberId already exists, it is left
 * alone (we do NOT overwrite stats or claim state).
 *
 * Returns: { memberId, created: boolean }
 */
export const addMember = async (
  teamId,
  { email, displayName, role = 'player', createdBy = null }
) => {
  if (!teamId) throw new Error('addMember: teamId required');
  if (!isValidEmail(email)) throw new Error('addMember: invalid email');

  const memberId = memberIdFromEmail(email);
  const ref = doc(db, 'teams', teamId, 'members', memberId);
  const existing = await getDoc(ref);

  if (existing.exists()) {
    const legacy = await ensureLegacyPlayerRecord(teamId, {
      email,
      displayName,
    });
    return { memberId, created: false, legacyPlayerId: legacy.legacyPlayerId };
  }

  await setDoc(ref, {
    email: normalizeEmail(email),
    displayName: (displayName || '').trim() || normalizeEmail(email),
    role,
    uid: null,
    claimed: false,
    invitedAt: serverTimestamp(),
    claimedAt: null,
    createdBy: createdBy || null,
    goals: 0,
    assists: 0,
    matchesPlayed: 0,
    stats: {},
    removed: false,
  });

  const legacy = await ensureLegacyPlayerRecord(teamId, {
    email,
    displayName,
  });

  return {
    memberId,
    created: true,
    legacyPlayerId: legacy.legacyPlayerId,
  };
};

/**
 * Ensure the team owner also has a claimed member slot.
 * Used for:
 *   - newly created teams
 *   - legacy teams created before the members model existed
 *
 * Returns: { memberId, created: boolean }
 */
export const ensureOwnerMembership = async (
  teamId,
  { uid, email, displayName = '', createdBy = null }
) => {
  if (!teamId) throw new Error('ensureOwnerMembership: teamId required');
  if (!uid) throw new Error('ensureOwnerMembership: uid required');
  if (!isValidEmail(email)) {
    throw new Error('ensureOwnerMembership: invalid email');
  }

  const normalized = normalizeEmail(email);
  const memberId = memberIdFromEmail(normalized);
  const ref = doc(db, 'teams', teamId, 'members', memberId);
  const existing = await getDoc(ref);

  if (existing.exists()) {
    return { memberId, created: false, legacyPlayerId: null };
  }

  await setDoc(ref, {
    email: normalized,
    displayName: (displayName || '').trim() || normalized,
    role: 'owner',
    uid,
    claimed: true,
    invitedAt: serverTimestamp(),
    claimedAt: serverTimestamp(),
    createdBy: createdBy || uid,
    goals: 0,
    assists: 0,
    matchesPlayed: 0,
    stats: {},
    removed: false,
  });

  return {
    memberId,
    created: true,
    legacyPlayerId: null,
  };
};

export const migrateLegacyPlayerToMember = async (
  teamId,
  { legacyPlayerId, memberEmail, displayName }
) => {
  if (!teamId) throw new Error('migrateLegacyPlayerToMember: teamId required');
  if (!legacyPlayerId) {
    throw new Error('migrateLegacyPlayerToMember: legacyPlayerId required');
  }
  if (!isValidEmail(memberEmail)) {
    throw new Error('migrateLegacyPlayerToMember: invalid memberEmail');
  }

  const memberId = memberIdFromEmail(memberEmail);
  const legacyRef = doc(db, 'teams', teamId, 'players', legacyPlayerId);
  const memberRef = doc(db, 'teams', teamId, 'members', memberId);

  const [legacySnap, memberSnap] = await Promise.all([
    getDoc(legacyRef),
    getDoc(memberRef),
  ]);

  if (!legacySnap.exists() || !memberSnap.exists()) {
    return { migrated: false, memberId, legacyPlayerId };
  }

  const legacy = legacySnap.data();
  const member = memberSnap.data();
  if (legacy.migratedToMemberId === memberId) {
    return { migrated: false, memberId, legacyPlayerId };
  }

  await updateDoc(memberRef, {
    displayName: (displayName || legacy.name || member.displayName || '').trim(),
    goals: legacy.goals || member.goals || 0,
    assists: legacy.assists || member.assists || 0,
    matchesPlayed: legacy.matchesPlayed || member.matchesPlayed || 0,
    stats:
      legacy.stats && Object.keys(legacy.stats).length > 0
        ? legacy.stats
        : (member.stats || {}),
  });

  await updateDoc(legacyRef, {
    migratedToMemberId: memberId,
    migratedToEmail: normalizeEmail(memberEmail),
    migratedAt: serverTimestamp(),
  });

  return { migrated: true, memberId, legacyPlayerId };
};

/**
 * Fetch a single member by email within a team.
 * Returns the raw document data plus { id } or null.
 */
export const getMemberByEmail = async (teamId, email) => {
  if (!teamId || !email) return null;
  const memberId = memberIdFromEmail(email);
  const snap = await getDoc(doc(db, 'teams', teamId, 'members', memberId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

/**
 * Claim all unclaimed member slots with the given email for the
 * given auth uid. Runs a collection-group query across all teams
 * and updates every shadow slot in one batch.
 *
 * Returns an array of { teamId, memberId } that were claimed.
 */
const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s || '');

export const claimMembership = async (uid, email, displayName = '') => {
  if (!uid || !isValidEmail(email)) return [];
  const normalized = normalizeEmail(email);
  const memberId = memberIdFromEmail(normalized);

  const buildPatch = (currentDisplayName) => {
    const patch = { uid, claimed: true, claimedAt: serverTimestamp() };
    if (displayName && !looksLikeEmail(displayName) &&
        (!currentDisplayName || looksLikeEmail(currentDisplayName))) {
      patch.displayName = displayName;
    }
    return patch;
  };

  try {
    const q = query(
      collectionGroup(db, 'members'),
      where('email', '==', normalized)
    );

    const snap = await getDocs(q);
    if (!snap.empty) {
      const batch = writeBatch(db);
      const results = [];

      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.claimed && !looksLikeEmail(data.displayName)) return;
        batch.update(d.ref, buildPatch(data.displayName));
        const parts = d.ref.path.split('/');
        results.push({ teamId: parts[1], memberId: parts[3] });
      });

      if (results.length > 0) {
        await batch.commit();
      }
      return results;
    }
  } catch (error) {
    console.warn('claimMembership: collectionGroup fallback na direct scan:', error);
  }

  const teamIds = await getAllTeamIds();
  const refs = teamIds.map((teamId) => ({
    teamId,
    ref: doc(db, 'teams', teamId, 'members', memberId),
  }));

  const memberSnaps = await Promise.all(
    refs.map(async ({ teamId, ref }) => {
      try {
        const snap = await getDoc(ref);
        return { teamId, ref, snap };
      } catch (error) {
        return { teamId, ref, snap: null, error };
      }
    })
  );

  const claimable = memberSnaps.filter(
    ({ snap }) =>
      snap &&
      snap.exists() &&
      !snap.data().removed &&
      normalizeEmail(snap.data().email) === normalized &&
      (!snap.data().claimed || looksLikeEmail(snap.data().displayName))
  );

  if (claimable.length === 0) return [];

  const batch = writeBatch(db);
  claimable.forEach(({ snap: s, ref }) => {
    batch.update(ref, buildPatch(s.data().displayName));
  });
  await batch.commit();

  return claimable.map(({ teamId }) => ({ teamId, memberId }));
};

export const getMembershipsByEmail = async (email) => {
  if (!isValidEmail(email)) return [];
  const normalized = normalizeEmail(email);
  const memberId = memberIdFromEmail(normalized);

  try {
    const q = query(
      collectionGroup(db, 'members'),
      where('email', '==', normalized)
    );

    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const parts = d.ref.path.split('/');
      return { teamId: parts[1], memberId: parts[3], ...d.data() };
    });
  } catch (error) {
    console.warn('getMembershipsByEmail: collectionGroup fallback na direct scan:', error);
  }

  const teamIds = await getAllTeamIds();
  const snaps = await Promise.all(
    teamIds.map(async (teamId) => {
      try {
        const snap = await getDoc(doc(db, 'teams', teamId, 'members', memberId));
        if (!snap.exists() || snap.data().removed) return null;
        return { teamId, memberId, ...snap.data() };
      } catch (error) {
        return null;
      }
    })
  );

  return snaps.filter(Boolean);
};

export const getAccessibleMemberships = async ({ uid, email }) => {
  const [claimedRows, emailRows] = await Promise.all([
    getUserMemberships(uid).catch(() => []),
    getMembershipsByEmail(email).catch(() => []),
  ]);

  const byKey = new Map();

  emailRows.forEach((row) => {
    byKey.set(`${row.teamId}:${row.memberId}`, {
      ...row,
      access: row.claimed ? 'email+claimed' : 'email-pending',
    });
  });

  claimedRows.forEach((row) => {
    byKey.set(`${row.teamId}:${row.memberId}`, {
      ...byKey.get(`${row.teamId}:${row.memberId}`),
      ...row,
      access: 'uid',
    });
  });

  return Array.from(byKey.values()).sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '')
  );
};

/**
 * Return all members across all teams for a given auth uid.
 * Used by MyDashboard to show "teams I belong to".
 *
 * Returns array of { teamId, memberId, ...data }.
 */
export const getUserMemberships = async (uid) => {
  if (!uid) return [];

  const q = query(
    collectionGroup(db, 'members'),
    where('uid', '==', uid)
  );

  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => {
    const parts = d.ref.path.split('/');
    return { teamId: parts[1], memberId: parts[3], ...d.data() };
  });
  rows.sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '')
  );
  return rows;
};

/**
 * List every member of a given team, sorted by displayName.
 * Caller is responsible for deciding whether to also merge legacy
 * `teams/{teamId}/players` entries.
 */
export const listTeamMembers = async (teamId) => {
  if (!teamId) return [];
  const snap = await getDocs(collection(db, 'teams', teamId, 'members'));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) =>
    (a.displayName || '').localeCompare(b.displayName || '')
  );
  return rows;
};
